import { axiosInstance } from 'v3/api/core/apiConfig';
import { PaymentParams } from 'v3/api/client/paymentParams';
import { RootState } from 'store';

type StartDealProps = {
  body: {
    dealValue: number;
    groupId: string;
    firstName: string;
    middleName?: string;
    lastName: string;
    sex: string;
    dateOfBirth: string;
    email: string;
    address: {
      postalCode: string;
      state: string;
      city: string;
      line1: string;
    };
    userPhone: string;
    /* Whether the member opted in to SMS. Optional by design, so false is the normal
       case and not a missing value. */
    smsConsent: boolean;
    half_payment_selected: boolean;
    months: string;
    down_payment: number | string;
    seats?: number;
  };
  testFlag: string;
};

type ReferralCodeProps = {
  detail: string;
  referralCode: string;
  shortDescription: string;
  valid: boolean;
};

type WaitingListProps = {
  body: {
    firstName: string;
    lastName: string;
    email: string;
    zipCode: string;
    group: string;
    referralCode: string;
  };
};

type SubscribeToWaitingListProps = {
  body: {
    inviteEmails: string[];
  };
  id: number;
};

/* No downPayment, and no halfPaymentSelected: the review-offer endpoint accepts
   neither. Half payment is granted by the referral code alone, so the client has no
   say in it. */
type ReviewOfferProps = {
  cfUuid: string;
  advanceStep?: boolean;
  months: number;
  dealValue?: number;
  weeklyCycle?: boolean;
  testFlag: string;
};

type DealChancesProps = {
  vehiclePrice: number;
  downPayment: number;
  term: string;
  creditScore: number;
  halfPaymentSelected: boolean;
};

export type Term = 24 | 36 | 48 | 60 | 72 | 84;

export type Offer = {
  savingsScore: number;
  savingsScoreGroup: number;
  managementFee: number;
  apy: number;
  monthlyPayment: number;
  weeklyPayment: number;
  weekTerm: number;
  groupValueWithFee: number;
  firstPayment?: number;
  firstWeeklyPayment?: number;
  finalPayment: number;
  traditionalMonthlyPayment: number;
  totalSavings: number;
  referralCodeSavings: number;
};

type HalfPaymentResult = {
  halfPayment: number;
  commonFundHalfPayment: number;
  admFeeMonthlyPayment: number;
};

type CalculateFeeResult = {
  data: Offer;
};

type ContractFlowResult = RootState['lead']['leadData'];

export const ContractFlowClient = () => {
  function getQueryTest(testFlag: string) {
    return testFlag ? `?test=${testFlag}` : '';
  }

  async function getGroupInfo(groupId: number | string) {
    try {
      const res = await axiosInstance.get(`/public/groups/${groupId}`);
      const data = res.data.data;
      return { data };
    } catch (error: any) {
      return { error };
    }
  }

  async function startDeal({ body, testFlag }: StartDealProps) {
    try {
      const queryTest = getQueryTest(testFlag);
      const res = await axiosInstance.post<{
        data: { result: ContractFlowResult };
      }>(`/v2/contract-flow/user-info/${queryTest}`, body);
      const { data } = res.data;
      return { data };
    } catch (error) {
      return { error };
    }
  }

  async function getReferralCode(code: string, clubId?: string) {
    try {
      let endpoint = `/referral-codes/${code}`;
      if (clubId) endpoint = `${endpoint}/?club_id=${clubId}`;

      const res = await axiosInstance.get<ReferralCodeProps>(endpoint);

      const { data } = res;

      return { data };
    } catch (error: any) {
      const errorData = {
        status: error?.response?.status ?? 0,
        message: error?.response?.data?.error?.errorList?.nonFieldErrors?.[0] ?? error?.message,
      };
      return { error: errorData };
    }
  }

  async function subscribeToWaitingList({ body }: WaitingListProps) {
    try {
      const res = await axiosInstance.post('/waitinglist', body);

      const { data } = res;

      return { data };
    } catch (error: any) {
      return { error };
    }
  }

  async function inviteEmailsToWaitingList({
    id,
    body,
  }: SubscribeToWaitingListProps) {
    try {
      await axiosInstance.patch(`/mailing/waitinglist/${id}/`, body);
    } catch (error: any) {
      return { error };
    }
  }

  async function updateReviewOffer({
    cfUuid,
    advanceStep = true,
    testFlag,
    ...rest
  }: ReviewOfferProps) {
    try {
      const queryTest = getQueryTest(testFlag);

      // advance_step drives whether the backend moves the member on to signing:
      // false for Recalculate, true for the "next step" submit.
      const { data } = await axiosInstance.patch<{
        data: { result: ContractFlowResult };
      }>(`/v2/contract-flow/review-offer/${cfUuid}/${queryTest}`, {
        advance_step: advanceStep,
        ...rest,
      });

      return { data: data.data.result };
    } catch (error) {
      return { error };
    }
  }

  async function calculateFee(cfUuid: string, term: number, downPayment = 0) {
    try {
      const {
        data: { data },
      } = await axiosInstance.get<CalculateFeeResult>(
        `/v2/contract-flow/calculate-fee/`,
        {
          params: { uuid: cfUuid, term, down_payment: downPayment },
        }
      );

      return { data };
    } catch (error) {
      return { error };
    }
  }

  async function getDealChances({
    vehiclePrice,
    term,
    creditScore,
    downPayment,
    halfPaymentSelected,
  }: DealChancesProps) {
    try {
      const { data } = await axiosInstance.get<{
        userChanceRange: [number, number];
      }>(`/v2/contract-flow/deal-chances/`, {
        params: {
          vehicle_price: vehiclePrice,
          loan_term: term,
          credit_score: creditScore,
          down_payment: downPayment,
          ...(halfPaymentSelected && { half_payment: 1 }),
        },
      });

      return { data };
    } catch (error) {
      return { error };
    }
  }

  async function getHalfPayment(managementFee: number, monthlyPayment: number) {
    try {
      const { data } = await axiosInstance.get<{ data: HalfPaymentResult }>(
        `/half-payment/?managementFee=${managementFee}&monthlyPayment=${monthlyPayment}`
      );

      return { data };
    } catch (error) {
      return { error };
    }
  }

  async function getContractDocument(contractToken: string) {
    try {
      const res = await axiosInstance.get(
        `/v2/contract-flow/${contractToken}/contract/`
      );
      const data = res.data;
      return { data };
    } catch (error) {
      return { error };
    }
  }

  async function signContract(
    cfUuid: string,
    signature: string,
    paymentParams: PaymentParams,
  ) {
    try {
      const { data } = await axiosInstance.patch<{
        data: { result: ContractFlowResult };
      }>(`/v2/contract-flow/sign-contract/${cfUuid}/`, {
        signature,
        ...paymentParams,
      });
      return { data: data.data.result };

    } catch (error: any) {
      const errorData = error?.response?.data?.error;
      return { error: errorData || error };
    }
  }

  async function applyReferral(cfUuid: string, referral: string) {
    try {
      const { data } = await axiosInstance.patch<{
        data: { result: ContractFlowResult };
      }>(`/v2/contract-flow/${cfUuid}/referral/`, {
        referral,
      });

      return { data: data.data.result, error: null };
    } catch (error) {
      return { data: null, error };
    }
  }

  async function updateContractFlow(uuid: string, body: any, testFlag: string) {
    try {
      const queryTest = getQueryTest(testFlag);
      const res = await axiosInstance.patch(
        `/v2/contract-flow/${uuid}/${queryTest}`,
        body
      );
      const data = res.data.data.result;
      return [data, undefined];
    } catch (error) {
      return [undefined, error];
    }
  }

  async function getContractFlowInfo(uuid: any) {
    try {
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      const res = await axiosInstance.get(`/v2/contract-flow/${uuid}/`);
      const data = res.data.data.result;
      return [data, undefined];
    } catch (error) {
      return [undefined, error];
    }
  }

  return {
    getGroupInfo,
    startDeal,
    getReferralCode,
    subscribeToWaitingList,
    inviteEmailsToWaitingList,
    updateReviewOffer,
    calculateFee,
    getDealChances,
    getHalfPayment,
    getContractDocument,
    signContract,
    applyReferral,
    updateContractFlow,
    getContractFlowInfo,
  };
};
